import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createBrowserRouter, RouterProvider, type RouteObject } from "react-router";

import { Layout } from "./components/Layout";
import { ToastProvider } from "./components/Toast";
import { DashboardPage } from "./features/account/DashboardPage";
import { SecurityPage } from "./features/account/SecurityPage";
import { LoginPage } from "./features/auth/LoginPage";
import { RegisterPage } from "./features/auth/RegisterPage";
import { ForgotPasswordPage, ResetPasswordPage, VerifyEmailPage } from "./features/auth/TokenPages";
import { ProfilePage } from "./features/account/ProfilePage";
import { AcceptInvitationPage } from "./features/company/CompanyPage";
import { EmailedFilesPage, FilePackagePage, FilesPage } from "./features/files/FilesPage";
import { NewOrderPage } from "./features/orders/NewOrderPage";
import { OrderDetailPage, OrdersListPage, OrderSubmittedPage } from "./features/orders/OrdersPages";
import {
  StaffFilesPage,
  StaffNewPackagePage,
  StaffPackagePage,
} from "./features/staff/StaffFilesPage";
import {
  StaffCustomersPage,
  StaffLayout,
  StaffOrderPage,
  StaffQueuePage,
} from "./features/staff/StaffPages";
import {
  AdminAuditPage,
  AdminDeletionsPage,
  AdminJobsPage,
  AdminLayout,
  AdminNotificationsPage,
  AdminReportsPage,
  AdminServerPage,
  AdminSettingsPage,
  AdminStaffPage,
} from "./features/admin/AdminPages";
import { AdminFilesPage, AdminUserPage, AdminUsersPage } from "./features/admin/AdminAccounts";
import { AuthProvider } from "./lib/auth";
import { RedirectIfAuthed, RequireAuth, RequireStaff } from "./routes/guards";
import { HomePage, NotFoundPage, PrivacyPage, TermsPage } from "./routes/PublicPages";

const authed = (el: React.ReactNode) => <RequireAuth>{el}</RequireAuth>;
const staffOnly = (el: React.ReactNode) => <RequireStaff>{el}</RequireStaff>;
const guest = (el: React.ReactNode) => <RedirectIfAuthed>{el}</RedirectIfAuthed>;

export const routes: RouteObject[] = [
  {
    element: <Layout />,
    children: [
      { path: "/", element: guest(<HomePage />) },
      { path: "/login", element: guest(<LoginPage />) },
      { path: "/register", element: guest(<RegisterPage />) },
      { path: "/verify-email", element: <VerifyEmailPage /> },
      { path: "/forgot-password", element: <ForgotPasswordPage /> },
      { path: "/reset-password", element: <ResetPasswordPage /> },
      { path: "/privacy", element: <PrivacyPage /> },
      { path: "/terms", element: <TermsPage /> },
      { path: "/dashboard", element: authed(<DashboardPage />) },
      { path: "/orders", element: authed(<OrdersListPage />) },
      { path: "/orders/new", element: authed(<NewOrderPage />) },
      { path: "/orders/:id", element: authed(<OrderDetailPage />) },
      { path: "/orders/:id/submitted", element: authed(<OrderSubmittedPage />) },
      { path: "/files", element: authed(<FilesPage />) },
      { path: "/files/:id", element: authed(<FilePackagePage />) },
      { path: "/f/:token", element: <EmailedFilesPage /> },
      { path: "/account", element: authed(<ProfilePage />) },
      { path: "/account/security", element: authed(<SecurityPage />) },
      { path: "/invitations/accept", element: authed(<AcceptInvitationPage />) },
      {
        path: "/staff",
        element: staffOnly(<StaffLayout />),
        children: [
          { index: true, element: <StaffQueuePage /> },
          { path: "orders/:id", element: <StaffOrderPage /> },
          { path: "files", element: <StaffFilesPage /> },
          { path: "files/new", element: <StaffNewPackagePage /> },
          { path: "files/:id", element: <StaffPackagePage /> },
          { path: "customers", element: <StaffCustomersPage /> },
        ],
      },
      {
        path: "/admin",
        element: <RequireStaff admin>{<AdminLayout />}</RequireStaff>,
        children: [
          { index: true, element: <AdminStaffPage /> },
          { path: "users", element: <AdminUsersPage /> },
          { path: "users/:id", element: <AdminUserPage /> },
          { path: "files", element: <AdminFilesPage /> },
          { path: "settings", element: <AdminSettingsPage /> },
          { path: "reports", element: <AdminReportsPage /> },
          { path: "notifications", element: <AdminNotificationsPage /> },
          { path: "audit", element: <AdminAuditPage /> },
          { path: "jobs", element: <AdminJobsPage /> },
          { path: "deletions", element: <AdminDeletionsPage /> },
          { path: "server", element: <AdminServerPage /> },
        ],
      },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
];

export function makeQueryClient() {
  return new QueryClient({
    defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
  });
}

export function Providers({
  client,
  children,
}: {
  client: QueryClient;
  children: React.ReactNode;
}) {
  return (
    <QueryClientProvider client={client}>
      <ToastProvider>
        <AuthProvider>{children}</AuthProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}

const router = createBrowserRouter(routes);
const queryClient = makeQueryClient();

export default function App() {
  return (
    <Providers client={queryClient}>
      <RouterProvider router={router} />
    </Providers>
  );
}
